<template>
  <section class="three-bet-cbet-explorer">
    <readonly-alert v-if="!authorized" />
    <saved-scenario-alert
      v-if="savedScenario"
      v-bind:title="savedScenario.title"
    />
    <header>
      <h1 class="page-header">
        3bet-cbet explorer
        <router-link to="/help/three-bet-cbet-explorer">
          <img src="/static/help.png" width="50">
        </router-link>
      </h1>
    </header>
    <section class="row">
      <section class="col-sm-6">
        <three-bet-cbet-explorer-inputs
          v-bind:state="state"
          v-bind:authorized="authorized"
        />
        <section class="hidden-xs">
          <hr>
          <create-sharable-url-button
            v-bind:disabled="!authorized"
            v-on:createSharableUrl="$emit('createSharableUrl', {
              method: 'createSharableThreeBetCbetExplorerURL',
              state: state,
            })"
          />
          <save-scenario-button
            v-bind:savedScenario="savedScenario"
            v-bind:disabled="!authorized"
            v-on:saveScenario="$emit('saveScenario', {
              method: 'createSharableThreeBetCbetExplorerURL',
              savedScenarioPropertyName: 'threeBetCbetExplorer',
              state: state,
            })"
            v-on:updateScenario="$emit('updateScenario', {
              method: '_constructThreeBetCbetExplorerData',
              state: state,
            })"
          />
        </section>
      </section>
      <section class="col-sm-6">
        <p class="breakeven-percentage">Breakeven percentage: {{ getBreakevenPercentage() }}%</p>
        <three-bet-cbet-explorer-graph
          v-bind:chartData="chartData"
        />
        <three-bet-cbet-explorer-table
          v-bind:selectedContinueVs3betPercentage="selectedContinueVs3betPercentage"
          v-bind:evForSelected="getEV(state, selectedContinueVs3betPercentage / 100)"
          v-bind:evForZeroPercent="getEV(state, 0)"
          v-bind:evForTwentyPercent="getEV(state, 0.2)"
          v-bind:evForFourtyPercent="getEV(state, 0.4)"
          v-bind:evForSixtyPercent="getEV(state, 0.6)"
          v-bind:evForEightyPercent="getEV(state, 0.8)"
          v-bind:evForOneHundredPercent="getEV(state, 1)"
        />
      </section>
    </section>
    <section class="visible-xs">
      <hr>
      <create-sharable-url-button
        v-bind:disabled="!authorized"
        v-on:createSharableUrl="$emit('createSharableUrl', {
          method: 'createSharableThreeBetCbetExplorerURL',
          state: state,
        })"
      />
      <save-scenario-button
        v-bind:savedScenario="savedScenario"
        v-bind:disabled="!authorized"
        v-on:saveScenario="$emit('saveScenario', {
          method: 'createSharableThreeBetCbetExplorerURL',
          savedScenarioPropertyName: 'threeBetCbetExplorer',
          state: state,
        })"
        v-on:updateScenario="$emit('updateScenario', {
          method: '_constructThreeBetCbetExplorerData',
          state: state,
        })"
      />
    </section>
    <onboarding-modal
      toolName="3bet-cbet explorer"
      urlPath="three-bet-cbet-explorer"
    />
  </section>
</template>

<script>
import ReadonlyAlert from '@/components/utilities/readonly-alert';
import SavedScenarioAlert from '@/components/utilities/saved-scenario-alert';
import ThreeBetCbetExplorerInputs from '@/components/three-bet-cbet-explorer/three-bet-cbet-explorer-inputs';
import ThreeBetCbetExplorerTable from '@/components/three-bet-cbet-explorer/three-bet-cbet-explorer-table';
import ThreeBetCbetExplorerGraph from '@/components/three-bet-cbet-explorer/three-bet-cbet-explorer-graph';
import CreateSharableUrlButton from '@/components/controls/create-sharable-url-button';
import SaveScenarioButton from '@/components/controls/save-scenario-button';
import OnboardingModal from '@/components/onboarding-modal';

export default {
  name: 'three-bet-cbet-explorer',
  components: {
    'readonly-alert': ReadonlyAlert,
    'saved-scenario-alert': SavedScenarioAlert,
    'three-bet-cbet-explorer-inputs': ThreeBetCbetExplorerInputs,
    'three-bet-cbet-explorer-table': ThreeBetCbetExplorerTable,
    'three-bet-cbet-explorer-graph': ThreeBetCbetExplorerGraph,
    'create-sharable-url-button': CreateSharableUrlButton,
    'save-scenario-button': SaveScenarioButton,
    'onboarding-modal': OnboardingModal,
  },
  data: function () {
    return {
      chartLabels: [ 0,5,10,15,20,25,30,35,40,45,50,55,60,65,70,75,80,85,90,95,100 ],
    };
  },
  computed: {
    state: function () {
      return this.$store.state.threeBetCbetExplorer;
    },
    savedScenario: function () {
      let routeId = this.$route.params.id;

      if (!this.authorized) {
        return false;
      }

      return this.$store.state.account.user.savedScenarios.threeBetCbetExplorer.find(function (savedScenario) {
        return savedScenario.url.includes(routeId);
      });
    },

    chartData: function () {
      return {
        labels: this.chartLabels,
        datasets: [{
          label: 'EV of 3betting',
          data: this._evOfThreeBettingData,
          pointRadius: this._evOfThreeBettingDataPointRadius,
          fill: false,
          borderColor: 'rgb(75, 192, 192)',
          lineTension: 0.1,
        }, {
          label: `EV = ${this.state.alternativeEV}`,
          data: this._alternativeEVData,
          fill: false,
          borderColor: 'gray',
          lineTension: 0.1
        }],
      };
    },
    _evOfThreeBettingData: function () {
      return this.chartLabels.map(function (callVs3betPercentage) {
        return this.getEV(this.state, (callVs3betPercentage / 100));
      }, this);
    },
    _evOfThreeBettingDataPointRadius: function () {
      let roundedBreakevenPercentage = this.selectedContinueVs3betPercentage.roundToNearestFive();

      return this.chartLabels.map(function (callVs3betPercentage) {
        if (callVs3betPercentage === roundedBreakevenPercentage) {
          return 7;
        }

        return 3;
      }, this);
    },
    _alternativeEVData: function () {
      return this.chartLabels.map(function () {
        return this.state.alternativeEV;
      }, this);
    },

    selectedContinueVs3betPercentage: function () {
      let numberOfSpecificHandsThatOpen = this.state.players.find(0).states[0].generalHands.numberOfSpecificHandsInRange;
      let numberOfSpecificHandsThatContinue = this.state.players.find(0).states[1].generalHands.numberOfSpecificHandsInRange;

      if (numberOfSpecificHandsThatContinue === 0) {
        return 0;
      }

      return ((numberOfSpecificHandsThatContinue / numberOfSpecificHandsThatOpen) * 100).roundToOneDecimalPlace();
    },
    authorized: function () {
      return this.$store.getters['account/authorizedForPremium'];
    },
  },
  created: function () {
    if (this.$route.params.id) {
      this.load();
    }
  },
  mounted: function () {
    this.handleOnboard();
  },
  methods: {
    getEV: function (state, continueVs3betProbability) {
      if (!this._ableToComputeEV(state)) {
        return null;
      }

      let playerBehindFourBetsEV = this._getPlayerBehindFourBetsEV(state);
      let initialRaiserFourBetsEV = this._getInitialRaiserFourBetsEV(state, continueVs3betProbability);
      let foldToThreeBetEV = this._getFoldToThreeBetEV(state, continueVs3betProbability);
      let foldToCbetEV = this._getFoldToCbetEV(state, continueVs3betProbability);
      let continueVsCbetEV = this._getContinueVsCbetEV(state, continueVs3betProbability);
      let oneStreetOfValueEV = this._getOneStreetOfValueEV(state, continueVs3betProbability);
      let twoStreetsOfValueEV = this._getTwoStreetsOfValueEV(state, continueVs3betProbability);
      let threeStreetsOfValueEV = this._getThreeStreetsOfValueEV(state, continueVs3betProbability);

      return (
        playerBehindFourBetsEV +
        initialRaiserFourBetsEV +
        foldToThreeBetEV +
        foldToCbetEV +
        continueVsCbetEV +
        oneStreetOfValueEV +
        twoStreetsOfValueEV +
        threeStreetsOfValueEV
      ).roundToTwoDecimalPlaces();
    },
    _ableToComputeEV: function (state) {
      let numOpenCombos = state.players.find(0).states[0].generalHands.numberOfSpecificHandsInRange;

      return numOpenCombos > 0;
    },
    __getProbabilityThatNoPlayerBehindFourBets: function (state) {
      let averageFourBetProbability = state.averageFourBetPercentage / 100;
      let averageNoFourBetProbability = 1 - averageFourBetProbability;

      return Math.pow(averageNoFourBetProbability, state.numPlayersLeftToAct);
    },
    __getProbabilityThatInitialRaiserFourBets: function (state, continueVs3betProbability) {
      let numOpenCombos = state.players.find(0).states[0].generalHands.numberOfSpecificHandsInRange;
      let numContinueCombos = numOpenCombos * continueVs3betProbability;
      let numFourBetCombos = state.players.find(0).states[2].generalHands.numberOfSpecificHandsInRange;

      if (numFourBetCombos > numContinueCombos) {
        numFourBetCombos = numContinueCombos;
      }

      return numFourBetCombos / numOpenCombos;
    },
    __getProbabilityThatInitialRaiserFolds: function (state, continueVs3betProbability) {
      let numOpenCombos = state.players.find(0).states[0].generalHands.numberOfSpecificHandsInRange;
      let numContinueCombos = numOpenCombos * continueVs3betProbability;
      let numFoldCombos = numOpenCombos - numContinueCombos;

      return numFoldCombos / numOpenCombos;
    },
    __getProbabilityThatInitialRaiserCalls: function (state, continueVs3betProbability) {
      let numOpenCombos = state.players.find(0).states[0].generalHands.numberOfSpecificHandsInRange;
      let numContinueCombos = numOpenCombos * continueVs3betProbability;
      let numFourBetCombos = state.players.find(0).states[2].generalHands.numberOfSpecificHandsInRange;
      let numCallCombos;

      if (numFourBetCombos > numContinueCombos) {
        numFourBetCombos = numContinueCombos;
      }

      numCallCombos = numContinueCombos - numFourBetCombos;

      return numCallCombos / numOpenCombos;
    },
    _getPlayerBehindFourBetsEV: function (state) {
      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatAPlayerBehindFourBets = 1 - probabilityThatNoPlayerBehindFourBets;
      let amountLost = state.threeBetSize;

      return (
        probabilityThatAPlayerBehindFourBets *
        -1 * amountLost
      );
    },
    _getInitialRaiserFourBetsEV: function (state, continueVs3betProbability) {
      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatInitialRaiserFourBets = this.__getProbabilityThatInitialRaiserFourBets(state, continueVs3betProbability);
      let amountLost = state.threeBetSize;

      return (
        probabilityThatNoPlayerBehindFourBets *
        probabilityThatInitialRaiserFourBets *
        -1 * amountLost
      );
    },
    _getFoldToThreeBetEV: function (state, continueVs3betProbability) {
      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatInitialRaiserFolds = this.__getProbabilityThatInitialRaiserFolds(state, continueVs3betProbability);
      let blinds = 1.5;
      let amountWon = blinds + state.openSize;

      return (
        probabilityThatNoPlayerBehindFourBets *
        probabilityThatInitialRaiserFolds *
        amountWon
      );
    },
    _getFoldToCbetEV: function (state, continueVs3betProbability) {
      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatInitialRaiserCalls = this.__getProbabilityThatInitialRaiserCalls(state, continueVs3betProbability);
      let probabilityOfMiss = 1 - (state.hitPercentage / 100);
      let probabilityOfFoldToCbet = 1 - (state.continueVsCbetPercentage / 100);
      let blinds = 1.5;
      let amountWon = blinds + state.threeBetSize;

      return (
        probabilityThatNoPlayerBehindFourBets *
        probabilityThatInitialRaiserCalls *
        probabilityOfMiss *
        probabilityOfFoldToCbet *
        amountWon
      );
    },
    _getContinueVsCbetEV: function (state, continueVs3betProbability) {
      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatInitialRaiserCalls = this.__getProbabilityThatInitialRaiserCalls(state, continueVs3betProbability);
      let probabilityOfMiss = 1 - (state.hitPercentage / 100);
      let probabilityOfContinueVsCbet = (state.continueVsCbetPercentage / 100);
      let blinds = 1.5;
      let flopPotSize = blinds + (2 * state.threeBetSize);
      let cbetAmount = state.cbetSize * flopPotSize;
      let amountLost = state.threeBetSize + cbetAmount;

      return (
        probabilityThatNoPlayerBehindFourBets *
        probabilityThatInitialRaiserCalls *
        probabilityOfMiss *
        probabilityOfContinueVsCbet *
        -1 * amountLost
      );
    },
    _getOneStreetOfValueEV: function (state, continueVs3betProbability) {
      if (state.hitPercentage === 0) {
        return 0;
      }

      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatInitialRaiserCalls = this.__getProbabilityThatInitialRaiserCalls(state, continueVs3betProbability);
      let probabilityOfHit = (state.hitPercentage / 100);
      let probabilityOfOneStreetOfValue = (state.firstStreetOfValuePercentage / 100) * (1 - (state.secondStreetOfValuePercentage / 100));
      let blinds = 1.5;
      let flopPotSize = blinds + (2 * state.threeBetSize);
      let flopBet = 0.75 * flopPotSize;
      let amountWon = blinds + state.threeBetSize + flopBet;

      return (
        probabilityThatNoPlayerBehindFourBets *
        probabilityThatInitialRaiserCalls *
        probabilityOfHit *
        probabilityOfOneStreetOfValue *
        amountWon
      );
    },
    _getTwoStreetsOfValueEV: function (state, continueVs3betProbability) {
      if (state.hitPercentage === 0) {
        return 0;
      }

      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatInitialRaiserCalls = this.__getProbabilityThatInitialRaiserCalls(state, continueVs3betProbability);
      let probabilityOfHit = (state.hitPercentage / 100);
      let probabilityOfTwoStreetsOfValue = (state.firstStreetOfValuePercentage / 100) * (state.secondStreetOfValuePercentage / 100) * (1 - (state.thirdStreetOfValuePercentage / 100));
      let blinds = 1.5;
      let flopPotSize = blinds + (2 * state.threeBetSize);
      let flopBet = 0.75 * flopPotSize;
      let turnPotSize = flopPotSize + (2 * flopBet);
      let turnBet = 0.75 * turnPotSize;
      let amountWon = blinds + state.threeBetSize + flopBet + turnBet;

      return (
        probabilityThatNoPlayerBehindFourBets *
        probabilityThatInitialRaiserCalls *
        probabilityOfHit *
        probabilityOfTwoStreetsOfValue *
        amountWon
      );
    },
    _getThreeStreetsOfValueEV: function (state, continueVs3betProbability) {
      if (state.hitPercentage === 0) {
        return 0;
      }

      let probabilityThatNoPlayerBehindFourBets = this.__getProbabilityThatNoPlayerBehindFourBets(state);
      let probabilityThatInitialRaiserCalls = this.__getProbabilityThatInitialRaiserCalls(state, continueVs3betProbability);
      let probabilityOfHit = (state.hitPercentage / 100);
      let probabilityOfThreeStreetsOfValue = (state.firstStreetOfValuePercentage / 100) * (state.secondStreetOfValuePercentage / 100) * (state.thirdStreetOfValuePercentage / 100);
      let blinds = 1.5;
      let flopPotSize = blinds + (2 * state.threeBetSize);
      let flopBet = 0.75 * flopPotSize;
      let turnPotSize = flopPotSize + (2 * flopBet);
      let turnBet = 0.75 * turnPotSize;
      let riverPotSize = turnPotSize + (2 * turnBet);
      let riverBet = 0.75 * riverPotSize;
      let amountWon = blinds + state.threeBetSize + flopBet + turnBet + riverBet;

      return (
        probabilityThatNoPlayerBehindFourBets *
        probabilityThatInitialRaiserCalls *
        probabilityOfHit *
        probabilityOfThreeStreetsOfValue *
        amountWon
      );
    },

    getBreakevenPercentage: function () {
      let currEV;

      for (let continueVs3betPercentage = 0; continueVs3betPercentage <= 100; continueVs3betPercentage += 0.01) {
        currEV = this.getEV(this.state, continueVs3betPercentage / 100);

        if (Math.abs(currEV - this.state.alternativeEV) < 0.02) {
          return continueVs3betPercentage.roundToOneDecimalPlace();
        }
      }
    },

    load: function () {
      let self = this;

      self.$http.get(`${process.env.BASE_API_URL}/sharable-url-store/` + self.$route.params.id)
        .then(function (response) {
          if (!response || !response.data.storeState) {
            alert('Didn\'t get data back from server. Redirecting to the default 3bet-cbet explorer page.');
            self.$router.push('/three-bet-cbet-explorer');
          } else {
            self.$store.commit('threeBetCbetExplorer/setState', response.data.storeState);
          }
        })
        .catch(function () {
          alert('URL doesn\'t exist. Redirecting to the default 3bet-cbet explorer page.');
          self.$router.push('/three-bet-cbet-explorer');
        })
      ;
    },
    handleOnboard: function () {
      let self = this;

      if (
        !self.authorized ||
        !self.$store.state.account.user.settings.onboarding ||
        self.$store.state.account.user.settings.onboarding.threeBetCbetExplorer
      ) {
        return;
      }

      $(self.$el).find('.onboarding-modal').modal();
      self.$http
        .patch(`${process.env.BASE_API_URL}/user/onboard`, { page: 'threeBetCbetExplorer' })
        .then(function () {
          self.$store.commit('account/onboard', 'threeBetCbetExplorer');
        })
        .catch(function () {
          console.log('Error: Unable to onboard.');
        })
      ;
    },
  },
};
</script>

<style lang="scss" scoped>
  .three-bet-cbet-explorer {
    font-size: 18px;

    .breakeven-percentage {
      text-align: center;
    }

    @media (min-width: 768px) {
      .col-sm-6 {
        height: calc(100vh - 160px) !important;
      }

      .col-sm-6:first-of-type {
        overflow-y: scroll;
      }
    }

    .three-bet-cbet-explorer-table {
      margin-top: 10px;
    }

    .well {
      overflow-x: scroll;
    }

    .help {
      margin-top: 10px;
    }

    .save-scenario-button-container {
      margin-top: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/three-bet-cbet-explorer/three-bet-cbet-explorer.vue